import { useId } from "react";
import type { PostgresWriteMode } from "./postgresPayload";
import { SelectDropdown, type SelectOption } from "./SelectDropdown";
import {
  FieldPairAside,
  FieldPairRow,
  FormGroup,
  FormSection,
  HelpText,
  Input,
  Label,
  SectionHeader,
  SectionText,
  SectionTitle,
} from "./styles";

const WRITE_MODE_OPTIONS: SelectOption<PostgresWriteMode>[] = [
  { value: "insert", label: "Insert (default)" },
  { value: "upsert", label: "Upsert" },
];

// Figma 16274-166334: the Write mode aside is wider than the 200px default.
const WRITE_MODE_WIDTH = 290;

interface PostgresFieldsProps {
  connectionString: string;
  setConnectionString: (value: string) => void;
  writeMode: PostgresWriteMode;
  setWriteMode: (value: PostgresWriteMode) => void;
}

// PostgreSQL card per Figma 16274-166328: a ruled header with the sink
// description, then the connection string and write mode on one row.
export function PostgresFields({
  connectionString,
  setConnectionString,
  writeMode,
  setWriteMode,
}: PostgresFieldsProps) {
  const connectionStringId = useId();

  return (
    <FormSection>
      <SectionHeader>
        <SectionTitle>PostgreSQL configuration</SectionTitle>
        <SectionText>
          Use this service as a JSON sink to an existing table. To read existing
          rows, create a scanner service targeting this PostgreSQL service.
        </SectionText>
      </SectionHeader>
      <FieldPairRow>
        <FormGroup>
          <Label htmlFor={connectionStringId}>Connection string*</Label>
          <Input
            id={connectionStringId}
            type="text"
            value={connectionString}
            onChange={(event) => setConnectionString(event.target.value)}
            placeholder="postgresql://user:password@host:5432/database"
            autoComplete="off"
            spellCheck={false}
            required
          />
          <HelpText>
            Use postgresql:// or postgres://. Include the database, credentials,
            and TLS options. The host must be reachable from the engine.
          </HelpText>
        </FormGroup>
        <FieldPairAside $width={WRITE_MODE_WIDTH}>
          <Label as="span">Write mode</Label>
          <SelectDropdown
            value={writeMode}
            options={WRITE_MODE_OPTIONS}
            onChange={setWriteMode}
          />
          <HelpText>
            Insert adds rows; Upsert updates rows by primary key, which the
            destination table must have.
          </HelpText>
        </FieldPairAside>
      </FieldPairRow>
    </FormSection>
  );
}
